import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import moment from "moment";
import Input from "./Input";
import Select from "./Select";
import { getProgramModalities } from "../services/soar";
import {
  getFieldDate,
  getFieldText,
  getFieldName,
  createEntity,
  updateEntity,
} from "./../../scripts/helperScripts";

function CareModalityModal({
  isOpen,
  hideModal,
  nextModal,
  isLastModal,
  clientId,
  setClientId,
  clientProgram,
  fosterHomePrograms,
  modalities,
  updateModalities,
  initialData,
}) {
  const [careModality, setCareModality] = useState({});
  const [initialRecordsProcessed, setInitialRecordsProcessed] = useState(false);
  const [clientProgramOptions, setClientProgramOptions] = useState([]);
  const [selectedClientProgram, setSelectedClientProgram] = useState(
    clientProgram?.ccx_client_programid
  );
  const [clientProgramsLoading, setClientProgramsLoading] = useState(false);
  const [careModalityOptions, setCareModalityOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const clientProgramAdmitDate = useMemo(() => {
    return getFieldDate(clientProgram?.ccx_admitdate);
  }, [clientProgram]);

  const joiSchema = useMemo(() => {
    return {
      ccx_caremodality: Joi.string().required().label("Care Modality"),
      ccx_startdate: Joi.date()
        .required()
        .min(moment(clientProgramAdmitDate).subtract(1, "day").toDate())
        .max(
          moment(clientProgramAdmitDate).isAfter(moment())
            ? moment(clientProgramAdmitDate).toDate()
            : "now"
        )
        .label("Effective Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "any.required":
                error.message = `Effective Date is required`;
                break;
              case "date.max":
                error.message = `Effective Date cannot be future dated beyond ${moment(
                  error.context.limit
                ).format("L")}`;
                break;
              case "date.min":
                error.message = `Effective Date cannot be before ${moment(
                  error.context.limit
                )
                  .add(1, "d")
                  .format("L")}`;
                break;
              default:
                error.message = `Effective Date is invalid`;
                break;
            }
          });
          return errors;
        }),
    };
  }, [clientProgramAdmitDate]);

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  const loadModalities = useCallback(
    async (startDate) => {
      setClientProgramOptions([]);
      setClientProgramsLoading(true);
      getProgramModalities(clientId, startDate).then((response) => {
        if (response && isMountedRef.current) {
          console.log("GetProgramModalities: ", response);
          const uniqueIds = [];
          const options = [];
          response.forEach((modality) => {
            if (
              !uniqueIds.includes(
                `${getFieldText(modality.sor_caremodality)}.${getFieldText(
                  modality.sor_programunit
                )}`
              )
            ) {
              uniqueIds.push(
                `${getFieldText(modality.sor_caremodality)}.${getFieldText(
                  modality.sor_programunit
                )}`
              );

              const option = {
                _id: getFieldText(modality.sor_caremodality),
                name: `${getFieldName(modality.sor_caremodality)} - ${moment(
                  getFieldDate(modality.sor_effdate)
                ).format("L")}${
                  modality.sor_expdate
                    ? ` - ${moment(getFieldDate(modality.sor_expdate)).format(
                        "L"
                      )}`
                    : ""
                }`,
                programUnit: getFieldText(modality.sor_programunit),
              };

              options.push(option);
            }
          });

          setCareModalityOptions(options);
          console.log("Care Modality Options: ", options);
          let availableClientPrograms = [];
          if (fosterHomePrograms && fosterHomePrograms.length > 0) {
            availableClientPrograms = fosterHomePrograms
              .filter(
                (program) =>
                  moment(getFieldDate(program.ccx_admitdate)).isSameOrBefore(
                    moment(startDate)
                  ) &&
                  (program.ccx_dischargedate === undefined ||
                    moment(
                      getFieldDate(program.ccx_dischargedate)
                    ).isSameOrAfter(moment(startDate)))
              )
              .map((program) => {
                return {
                  _id: program.ccx_client_programid,
                  name: `${getFieldName(program.ccx_programunit)} - ${moment(
                    getFieldDate(program.ccx_admitdate)
                  ).format("L")}${
                    program.ccx_dischargedate
                      ? ` - ${moment(
                          getFieldDate(program.ccx_dischargedate)
                        ).format("L")}`
                      : ""
                  }`,
                  programUnit: getFieldText(program.ccx_programunit),
                  clientProgramName: program.ccx_name,
                };
              });
          }
          if (
            options
              .map((option) => option.programUnit)
              .includes(getFieldText(clientProgram.ccx_programunit)) &&
            moment(getFieldDate(clientProgram.ccx_admitdate)).isSameOrBefore(
              moment(startDate)
            ) &&
            (clientProgram.ccx_dischargedate === undefined ||
              moment(
                getFieldDate(clientProgram.ccx_dischargedate)
              ).isSameOrAfter(startDate))
          ) {
            availableClientPrograms.push({
              _id: clientProgram.ccx_client_programid,
              name: `${getFieldName(clientProgram.ccx_programunit)} - ${moment(
                getFieldDate(clientProgram.ccx_admitdate)
              ).format("L")}${
                clientProgram.ccx_dischargedate
                  ? ` - ${moment(
                      getFieldDate(clientProgram.ccx_dischargedate)
                    ).format("L")}`
                  : ""
              }`,
              programUnit: getFieldText(clientProgram.ccx_programunit),
              clientProgramName: clientProgram.ccx_name,
            });
          }
          setClientProgramOptions(availableClientPrograms);

          if (availableClientPrograms.length === 1)
            setSelectedClientProgram(availableClientPrograms[0]._id);
          else {
            const fosterHomeClientProgram = availableClientPrograms.find(
              (program) => {
                let name = clientProgram.ccx_name;

                if (
                  getFieldName(clientProgram["unit.ccx_programtype"]) === "TFC"
                ) {
                  name = getFieldName(clientProgram.ccx_programunit).replace(
                    ` - ${getFieldName(clientProgram["unit.ccx_program"])}`,
                    ""
                  );
                }

                return program.name.includes(name);
              }
            );

            if (fosterHomeClientProgram)
              setSelectedClientProgram(fosterHomeClientProgram._id);
          }

          console.log("Available Program Units: ", availableClientPrograms);
        }
        setClientProgramsLoading(false);
      });
    },
    [clientId, clientProgram, fosterHomePrograms]
  );

  useEffect(() => {
    if (!initialRecordsProcessed) {
      if (initialData) {
        setCareModality(initialData);
        loadModalities(initialData.ccx_startdate);
      }

      setInitialRecordsProcessed(true);
    }
  }, [initialData, initialRecordsProcessed, loadModalities]);

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);

    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }
    const data = { ...careModality };
    data[input.name] = input.value;
    setCareModality(data);

    setErrors(errs);
  };

  const handleStartDateChange = ({ currentTarget: input }) => {
    handleChange({ currentTarget: input });

    if (input.value) {
      loadModalities(input.value);
    }
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(careModality, joiSchema, options);
    if (!error) return null;

    let validationErrors = {};
    for (let item of error.details)
      validationErrors[item.path[0]] = item.message;
    setErrors(validationErrors);
    return validationErrors;
  };

  const handleSave = () => {
    if (setClientId) {
      setClientId(null);
    }

    const clientProgramOption = clientProgramOptions.filter(
      (option) => option._id === selectedClientProgram
    )[0];
    const obj = {
      ccx_name: {
        type: "string",
        value: clientProgramOption.clientProgramName,
      },
      ccx_clientprogramid: {
        type: "entity",
        value: {
          entityName: "ccx_client_program",
          entityId: clientProgramOption._id,
        },
      },
      ccx_caremodality: {
        type: "optionset",
        value: parseInt(careModality.ccx_caremodality),
      },
      sor_contact: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: clientId,
        },
      },
      ccx_startdate: {
        type: "datetime",
        value: new Date(careModality.ccx_startdate),
      },
    };

    const toastId = toast.loading("Care Modality is being created...", {
      type: toast.TYPE.INFO,
    });
    createEntity(
      "ccx_caremodality",
      JSON.stringify(obj),
      safyInfo,
      getFieldName(clientProgram.ccx_company).split(" of ")[1]
    ).then(() => {
      if (modalities) {
        const prevModality = modalities.filter(
          (modality) =>
            modality.ccx_enddate == null &&
            getFieldText(modality.ccx_clientprogramid) ===
              clientProgramOption._id
        )[0];
        if (prevModality != null) {
          let endDate = careModality.ccx_startdate;
          if (
            new Date(prevModality.ccx_startdate) >
            new Date(careModality.ccx_startdate)
          )
            endDate = prevModality.ccx_startdate;

          const prevModalityObj = {
            ccx_enddate: {
              type: "datetime",
              value: new Date(endDate),
            },
          };

          toast.update(toastId, {
            render:
              "Care Modality has been created. Expiring the previous Care Modality...",
          });

          updateEntity(
            "ccx_caremodality",
            prevModality.ccx_caremodalityid,
            JSON.stringify(prevModalityObj),
            safyInfo
          )
            .then(() => {
              toast.update(toastId, {
                render: "Previous Care Modality has been expired.",
                type: toast.TYPE.SUCCESS,
                isLoading: false,
                autoClose: 5000,
                closeOnClick: true,
              });
              updateModalities && updateModalities(true);
            })
            .catch(() => {
              toast.update(toastId, {
                render:
                  "An error occurred while expiring the previous Care Modality.",
                type: toast.TYPE.SUCCESS,
                isLoading: false,
                autoClose: 5000,
                closeOnClick: true,
              });
            });
        } else {
          toast.update(toastId, {
            render: "Care Modality has been created.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
          updateModalities && updateModalities(true);
        }
      } else {
        toast.update(toastId, {
          render: "Care Modality has been created.",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
        updateModalities && updateModalities(true);
      }
    });
  };

  const handleSaveAndAdd = () => {
    handleSave();
    resetForm();
  };

  const resetForm = () => {
    setCareModality({ ccx_startdate: "" });
    setClientProgramOptions([]);
    setErrors({});
  };

  const handleHideModal = () => {
    resetForm();
    hideModal();
  };

  return (
    <>
      {isOpen ? (
        <Modal show={isOpen} animation={false}>
          <Modal.Header>
            <Modal.Title>Care Modality</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {clientProgram === undefined ? (
              <div>Client Program is being created...</div>
            ) : null}
            <Input
              name="ccx_startdate"
              label="Effective Date"
              type="date"
              value={careModality.ccx_startdate}
              error={errors.ccx_startdate}
              onChange={handleStartDateChange}
              disabled={clientProgram === undefined}
            />
            {careModality.ccx_startdate !== undefined &&
              careModality.ccx_startdate !== "" &&
              clientProgramOptions.length > 0 &&
              !clientProgramsLoading && (
                <Select
                  name="clientProgram"
                  label="Client Program"
                  options={clientProgramOptions}
                  value={selectedClientProgram}
                  onChange={(e) => {
                    setSelectedClientProgram(e.currentTarget.value);
                  }}
                  error={errors.clientProgram}
                  disabled={
                    careModality.ccx_startdate === undefined ||
                    careModality.ccx_startdate === ""
                  }
                />
              )}
            {clientProgramsLoading && (
              <div className="mb-2">Client Programs loading...</div>
            )}
            {careModality.ccx_startdate !== undefined &&
              careModality.ccx_startdate !== "" &&
              clientProgramOptions.length === 0 &&
              !clientProgramsLoading && (
                <div className="text-danger mb-2">
                  No Available Modalities for Client
                </div>
              )}
            <Select
              name="ccx_caremodality"
              label="Care Modality"
              options={careModalityOptions.filter((modality) =>
                clientProgramOptions.length > 0 &&
                selectedClientProgram !== undefined &&
                selectedClientProgram !== ""
                  ? modality.programUnit ===
                    clientProgramOptions.filter(
                      (option) => option._id === selectedClientProgram
                    )[0].programUnit
                  : []
              )}
              value={careModality.ccx_caremodality}
              onChange={handleChange}
              error={errors.ccx_caremodality}
              disabled={
                selectedClientProgram === undefined ||
                selectedClientProgram === ""
              }
            />
          </Modal.Body>
          <Modal.Footer>
            {nextModal === undefined && (
              <>
                <button className="btn btn-danger" onClick={handleHideModal}>
                  Cancel
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      handleHideModal();
                    }
                  }}
                >
                  Submit
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) handleSaveAndAdd();
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
            {nextModal && (
              <>
                <button className="btn btn-primary" onClick={nextModal}>
                  Skip
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      resetForm();
                      nextModal();
                    }
                  }}
                >
                  Submit and {isLastModal ? "End" : "Next"}
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) handleSaveAndAdd();
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default CareModalityModal;
